import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/img2.avif';
import { FiCheckCircle, FiShield, FiSettings, FiTrendingUp, FiAward, FiZap } from 'react-icons/fi';
import p5 from '../assets/p6.jpg';
import '../css/dualPlateCheckValves.css';

const keyFeatures = [
  {
    title: 'Compact Design',
    description: 'Dual plate check valves feature a compact wafer design that requires minimal space while providing superior flow characteristics.'
  },
  {
    title: 'Rapid Response',
    description: 'Spring-assisted dual plate mechanism ensures rapid response to flow reversal, preventing backflow and water hammer.'
  },
  {
    title: 'Low Pressure Drop',
    description: 'Streamlined flow path and dual plate design minimize pressure drop, ensuring optimal system efficiency.'
  },
  {
    title: 'Lightweight Construction',
    description: 'Lightweight wafer construction makes installation easier and reduces load on piping systems.'
  }
];

const specifications = [
  {
    icon: <FiSettings />,
    label: 'Size Range',
    value: '2" – 24"',
    detail: 'Standard and large diameter sizes available'
  },
  {
    icon: <FiTrendingUp />,
    label: 'Pressure Rating',
    value: 'Class 150 – 600',
    detail: 'Suitable for medium pressure applications'
  },
  {
    icon: <FiShield />,
    label: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Duplex',
    detail: 'Multiple material options for corrosion resistance'
  },
  {
    icon: <FiAward />,
    label: 'Standards',
    value: 'API 594, BS EN 593',
    detail: 'Compliant with international standards'
  }
];

const valveTypes = [
  {
    name: 'Wafer Type Dual Plate',
    description: 'Standard wafer-type design installed between two flanges, ideal for space-saving applications in pipelines.',
    features: ['Compact installation', 'Lightweight design', 'Easy maintenance', 'Cost-effective']
  },
  {
    name: 'Flanged Dual Plate',
    description: 'Flanged design with bolted connections, providing secure installation and easy access for maintenance.',
    features: ['Robust construction', 'Secure sealing', 'Standard connections', 'Easy access']
  },
  {
    name: 'High-Pressure Dual Plate',
    description: 'Heavy-duty design engineered for high-pressure applications requiring superior performance.',
    features: ['High-pressure capability', 'Enhanced durability', 'Reliable operation', 'Superior sealing']
  },
  {
    name: 'Silent Dual Plate',
    description: 'Specialized design with advanced spring mechanism to prevent water hammer and reduce noise.',
    features: ['Silent operation', 'Water hammer prevention', 'Smooth closing', 'Noise reduction']
  }
];

const advantages = [
  {
    icon: <FiZap />,
    title: 'Fast Closing Action',
    description: 'Spring-assisted dual plate mechanism provides rapid closing action, preventing backflow and protecting equipment from reverse flow damage.'
  },
  {
    icon: <FiShield />,
    title: 'Compact Installation',
    description: 'Wafer design requires minimal space between flanges, making dual plate check valves ideal for space-constrained installations.'
  },
  {
    icon: <FiSettings />,
    title: 'Cost-Effective Solution',
    description: 'Lightweight construction and simple design result in lower material costs and reduced installation time compared to swing check valves.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'Excellent Flow Efficiency',
    description: 'Streamlined flow path and dual plate design provide excellent flow characteristics with minimal pressure drop across the valve.'
  }
];

const applications = [
  {
    title: 'Water Distribution',
    description: 'Municipal water systems and distribution networks requiring compact backflow prevention valves.'
  },
  {
    title: 'HVAC Systems',
    description: 'Heating, ventilation, and air conditioning systems where compact design and low pressure drop are essential.'
  },
  {
    title: 'Industrial Process Lines',
    description: 'Industrial process lines requiring reliable check valve operation with minimal space requirements.'
  },
  {
    title: 'Fire Protection Systems',
    description: 'Fire protection systems where rapid response and reliable backflow prevention are critical.'
  },
  {
    title: 'Irrigation Systems',
    description: 'Agricultural and irrigation systems requiring cost-effective backflow prevention solutions.'
  },
  {
    title: 'Wastewater Treatment',
    description: 'Wastewater treatment facilities where compact design and reliable operation are essential.'
  }
];

const DualPlateCheckValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('dpcv-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current;
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Banner Section */}
      <section className="dpcv-hero">
        <div className="dpcv-hero-background">
          <img
            src={banner}
            alt="Dual Plate Check Valves Banner"
            className="dpcv-hero-image"
          />
          <div className="dpcv-hero-overlay"></div>
        </div>
        <div className="dpcv-hero-content">
          <h1 className="dpcv-hero-title">Dual Plate Check Valves</h1>
          <p className="dpcv-hero-subtitle">
            Compact and efficient dual plate check valves designed for space-saving backflow prevention
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="dpcv-section dpcv-intro"
      >
        <div className="dpcv-container">
          <div className="dpcv-intro-grid">
            <div className="dpcv-intro-text">
              <h2 className="dpcv-intro-title">Dual Plate Check Valve</h2>
              <p className="dpcv-intro-description">
                Dual Plate Check Valve is used to prevent back flow in the line. Unlike swing check 
                valve the operation is assisted by spring force. Most used standard of reference are 
                API 6D and API 594. Constructions with retainer less and with retainer pin are 
                available, together with the more traditional construction with hinge pin inserted from 
                the side. Materials available include carbon steel, low temperature carbon steel, low 
                alloy steel, austenitic stainless steels, austenitic-ferritic duplex stainless steel, nickel 
                alloys.
              </p>
            </div>
            <div className="dpcv-intro-image">
              <img src="/dualplate.PNG" alt="Dual Plate Check Valves" className="dpcv-intro-img" />
            </div>
          </div>
        </div>
      </div>

      {/* Product Range, Technical Specifications, and Design Features Section */}
      <div 
        ref={(el) => (sectionsRef.current[1] = el)}
        className="dpcv-section dpcv-specs"
      >
        <div className="dpcv-container">
          <div className="dpcv-hero-specs-row">
            <div className="dpcv-spec-card-horizontal">
              <h3>Product Range</h3>
              <ul>
                <li><strong>Piping component size:</strong> 2" ~ 24" (DN50-DN600)</li>
                <li><strong>Pressure:</strong> ANSI Class 150~2500</li>
                <li><strong>Temperature:</strong> -196℃~ 500℃</li>
                <li><strong>Body Materials:</strong> Carbon Steel, Stainless Steel, Alloy Steel, Duplex Steel</li>
                <li><strong>Trim (Disc/Stem):</strong> Carbon Steel, Stainless Steel, Duplex Steel, Alloy Steel</li>
              </ul>
            </div>
            <div className="dpcv-spec-card-horizontal">
              <h3>Technical Specifications</h3>
              <ul>
                <li><strong>Design:</strong> ASME B16.34/API 6D/API 594</li>
                <li><strong>Face to Face:</strong> API 6D/API 594</li>
                <li><strong>End Flange:</strong> ASME B16.5</li>
                <li><strong>BW End:</strong> ASME B16.25</li>
                <li><strong>Inspection and Testing:</strong> API 598/API 6D/ISO 5208</li>
                <li><strong>Special:</strong> NACE MR-01-75, IBR</li>
              </ul>
            </div>
            <div className="dpcv-spec-card-horizontal">
              <h3>Design Features</h3>
              <ul>
                <li>Body construction in Wafer, Lugged and Flanged</li>
                <li>Flanged, Butt Weld End connections</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      

      {/* Valve Types Section */}
      
      {/* Applications Section */}
      
    

      <InquiryBanner />
      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default DualPlateCheckValves;










